import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Phone, ExternalLink, MessageSquare, Eye, Code } from 'lucide-react';
import type { WhatsAppTemplate } from '@/api/tenants/tenants.types';

interface ButtonComponent {
    type: 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL';
    text: string;
    phone_number?: string;
    url?: string;
}

interface WhatsAppTemplatePreviewProps {
    template: WhatsAppTemplate;
    className?: string;
}

export const WhatsAppTemplatePreview: React.FC<WhatsAppTemplatePreviewProps> = ({
    template,
    className = ""
}) => {
    const [showVariables, setShowVariables] = useState(true);
    const [previewData, setPreviewData] = useState<{ [key: string]: string }>({
        '1': 'John Doe',
        '2': 'ORDER123',
        '3': 'Tomorrow',
        '4': 'Company Name',
        '5': 'Product Name'
    });

    // Render with highlighted variable placeholders or sample data
    const renderPreview = useCallback((text: string) => {
        if (!text) return text;
        
        if (showVariables) {
            // Show variables as highlighted placeholders
            return text.split(/(\{\{\d+\}\})/g).map((part, index) => {
                if (part.match(/\{\{\d+\}\}/)) {
                    return (
                        <span 
                            key={index}
                            className="inline-block bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded text-xs font-medium border border-blue-200 dark:border-blue-600"
                        >
                            {part}
                        </span>
                    );
                }
                return part;
            });
        } else {
            // Show with sample data
            return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
                return previewData[num] || match;
            });
        }
    }, [previewData, showVariables]);

    const hasVariables = template.components?.some(c => c.text?.includes('{{'));

    return (
        <div className={`${className}`}>
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Preview Variables */}
                <div className="space-y-4">
                    {hasVariables && showVariables && (
                        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-medium text-blue-800 dark:text-blue-200">Variable Placeholders</Label>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowVariables(false)}
                                    className="text-xs h-7"
                                >
                                    <Eye className="w-3 h-3 mr-1" />
                                    Show Sample Data
                                </Button>
                            </div>
                            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                                Variables like <code className="bg-blue-100 dark:bg-blue-800/60 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded font-medium">{'{{1}}'}</code> are highlighted in blue in the preview. 
                                When sending messages, these will be replaced with actual data.
                            </p>
                        </div>
                    )}
                    
                    {hasVariables && !showVariables && (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-xs font-medium">Preview Variables</Label>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowVariables(true)}
                                    className="text-xs h-7"
                                >
                                    <Code className="w-3 h-3 mr-1" />
                                    Show Variables
                                </Button>
                            </div>
                            <div className="space-y-3">
                                {[1, 2, 3, 4, 5].map(num => {
                                    const hasVariable = template.components?.some(c => c.text?.includes(`{{${num}}}`));
                                    if (!hasVariable) return null;
                                    return (
                                        <div key={num}>
                                            <Label className="text-xs">{`{{${num}}}`}</Label>
                                            <Input
                                                value={previewData[num.toString()] || ''}
                                                onChange={(e) => setPreviewData(prev => ({
                                                    ...prev,
                                                    [num.toString()]: e.target.value
                                                }))}
                                                placeholder={`Value ${num}`}
                                                className="h-8 text-xs mt-1"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}


                </div>

                {/* Right Column - WhatsApp Preview */}
                <div className="flex justify-center">
                    {/* WhatsApp Chat Interface - Exact copy from submit dialog */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border max-w-md w-full overflow-hidden">
                {/* WhatsApp Header */}
                <div className="bg-[#075E54] text-white p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <MessageSquare className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="font-medium text-sm">Your Business</p>
                        <p className="text-xs opacity-90">online</p>
                    </div>
                </div>

                {/* Chat Background with Authentic WhatsApp Pattern */}
                <div 
                    className="min-h-[320px] p-3 relative"
                    style={{
                        backgroundColor: '#E5DDD5',
                        backgroundImage: `url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'repeat',
                        backgroundPosition: 'center',
                        backgroundAttachment: 'local',
                        margin: 0,
                        padding: '12px'
                    }}
                >
                    {template.components && template.components.length > 0 ? (
                        <>
                            {/* WhatsApp Message Bubble */}
                            <div className="flex justify-end mb-4">
                                <div className="max-w-[320px]">
                                    {/* Template Message Label */}
                                    <div className="text-center mb-2">
                                        <span className="inline-block bg-white/80 text-gray-600 text-xs px-2 py-1 rounded-full">
                                            Template Message
                                        </span>
                                    </div>
                                    
                                    {/* Message Bubble */}
                                    <div className="bg-[#DCF8C6] rounded-lg p-3 shadow-sm relative">
                                        {/* Message Tail */}
                                        <div className="absolute -right-2 top-3 w-0 h-0 border-l-[8px] border-l-[#DCF8C6] border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                                        
                                        {/* Message Content */}
                                        <div className="space-y-2">
                                            {template.components.map((component) => (
                                                <div key={`preview-${component.type}`}>
                                                    {component.type === 'HEADER' && component.text && (
                                                        <div className="font-semibold text-sm text-gray-900 mb-1">
                                                            {renderPreview(component.text)}
                                                        </div>
                                                    )}
                                                    
                                                    {component.type === 'BODY' && component.text && (
                                                        <div className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                                                            {renderPreview(component.text)}
                                                        </div>
                                                    )}
                                                    
                                                    {component.type === 'FOOTER' && component.text && (
                                                        <div className="text-xs text-gray-600 mt-2 italic">
                                                            {renderPreview(component.text)}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            
                                            {/* Time stamp */}
                                            <div className="flex justify-end items-center gap-1 mt-2">
                                                <span className="text-xs text-gray-500">
                                                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                                <div className="flex">
                                                    <div className="w-3 h-3 text-gray-500">
                                                        <svg viewBox="0 0 16 15" className="fill-current">
                                                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l3.61 3.667c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.51z"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Buttons outside message bubble - Exact WhatsApp layout */}
                                    {template.components.some(c => c.type === 'BUTTONS' && c.buttons && c.buttons.length > 0) && (
                                        <div className="mt-1">
                                            {(() => {
                                                const buttons = template.components.find(c => c.type === 'BUTTONS')?.buttons || [];
                                                
                                                if (buttons.length === 1) {
                                                    // Single button - full width
                                                    const typedButton = buttons[0] as ButtonComponent;
                                                    return (
                                                        <div className="bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm">
                                                            <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                <span className="truncate">{typedButton.text}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                } else if (buttons.length === 2) {
                                                    // Two buttons - vertically stacked
                                                    return (
                                                        <div className="space-y-1">
                                                            {buttons.map((button, buttonIndex) => {
                                                                const typedButton = button as ButtonComponent;
                                                                return (
                                                                    <div
                                                                        key={buttonIndex}
                                                                        className="bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm"
                                                                    >
                                                                        <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                            {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                            {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                            {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                            <span className="truncate">{typedButton.text}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                } else if (buttons.length === 3) {
                                                    // Three buttons - 2+1 layout (like WhatsApp)
                                                    return (
                                                        <div className="space-y-1">
                                                            {/* First row - 2 buttons side by side */}
                                                            <div className="flex gap-1">
                                                                {buttons.slice(0, 2).map((button, buttonIndex) => {
                                                                    const typedButton = button as ButtonComponent;
                                                                    return (
                                                                        <div
                                                                            key={buttonIndex}
                                                                            className="flex-1 bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm"
                                                                        >
                                                                            <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                                {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                                {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                                {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                                <span className="truncate">{typedButton.text}</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            {/* Second row - 1 button full width */}
                                                            <div>
                                                                {(() => {
                                                                    const typedButton = buttons[2] as ButtonComponent;
                                                                    return (
                                                                        <div className="bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm">
                                                                            <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                                {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                                {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                                {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                                <span className="truncate">{typedButton.text}</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-32 text-gray-500">
                            <div className="text-center bg-white/60 rounded-lg p-4">
                                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Your template preview will appear here</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
                </div>
            </div>
        </div>
    );
};
